export type DocCategory = 'Start' | 'Core Editing' | 'Creative Tools' | 'Output' | 'Reference'

export interface DocFigure {
  /** Imported asset URL (import the image in the page module and pass it here). */
  src: string
  /** Required alt text for accessibility. */
  alt: string
  /** Optional caption shown under the image. */
  caption?: string
}

/**
 * Rich content blocks a section can render. Text in `paragraph`, `list`, `steps`,
 * and `note` supports inline markup: `**bold**` for UI labels and `` `code` `` for
 * keys and file names (see RichText in docs-shell).
 */
export type DocBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'steps'; items: string[] }
  | { kind: 'note'; tone?: 'tip' | 'warning' | 'info'; text: string }
  | { kind: 'table'; headers: string[]; rows: string[][] }
  | { kind: 'figure'; figure: DocFigure }

export interface DocSection {
  title: string
  /** Shorthand: rendered as a bullet list. Ignored when `blocks` is provided. */
  items?: string[]
  /** Richer content. Takes precedence over `items` when present. */
  blocks?: DocBlock[]
}

export interface DocPage {
  slug: string
  title: string
  description: string
  category: DocCategory
  sections: DocSection[]
  /** Slugs of related pages, shown as links at the foot of the article. */
  related?: string[]
}

export interface DocPageContent extends DocPage {
  order: number
}

type DocPageModule = { default: DocPageContent }

const pageModules = import.meta.glob<DocPageModule>('./pages/*.ts', { eager: true })

const orderedDocPageContent = Object.values(pageModules)
  .map((module) => module.default)
  .sort((a, b) => a.order - b.order)

export const DOC_PAGES: DocPage[] = orderedDocPageContent.map(({ order: _order, ...page }) => page)

export const DOC_GROUPS: DocCategory[] = [
  'Start',
  'Core Editing',
  'Creative Tools',
  'Output',
  'Reference',
]

export function getDocPage(slug: string): DocPage | undefined {
  return DOC_PAGES.find((page) => page.slug === slug)
}

/** Slugs hidden from docs nav while AI creation is disabled. Empty: the Local AI
 * guide was removed because TTS, music generation, and transcription are not
 * part of KWYKEDIT. Kept as a hook so future gated pages need no plumbing. */
export const HIDDEN_DOC_SLUGS_WHEN_AI_DISABLED: readonly string[] = []

/**
 * Pages listed in docs navigation, search, home cards, related links, and the
 * prev/next footer. KWYKEDIT filters the Local AI guide behind
 * `ENABLE_AI_CREATION` (default false); the page module itself is untouched
 * and stays reachable by direct URL. Pass the flag in so this module stays a
 * pure content registry (docs-shell supplies the flag value).
 */
export function getVisibleDocPages(aiCreationEnabled: boolean): DocPage[] {
  if (aiCreationEnabled) return DOC_PAGES
  return DOC_PAGES.filter((page) => !HIDDEN_DOC_SLUGS_WHEN_AI_DISABLED.includes(page.slug))
}